import { useState, useEffect } from "react";
import { motion, useScroll } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_ITEMS, LINKS } from "@/data/content";
import { useLanguage, useT } from "@/i18n/LanguageContext";

const LangToggle = ({ variant = "desktop", className = "" }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div
      data-testid={`lang-toggle-${variant}`}
      className={`inline-flex items-center rounded-full border border-[#E8E7E2] bg-[#F4F2EC]/70 p-0.5 text-xs font-semibold ${className}`}
    >
      <button
        type="button"
        onClick={() => setLang("sr")}
        data-testid={`lang-sr-${variant}`}
        className={`px-3 py-1 rounded-full transition-colors duration-200 ${
          lang === "sr" ? "bg-[#0A0A0F] text-white" : "text-[#62646B] hover:text-[#0A0A0F]"
        }`}
        aria-pressed={lang === "sr"}
      >
        SR
      </button>
      <button
        type="button"
        onClick={() => setLang("en")}
        data-testid={`lang-en-${variant}`}
        className={`px-3 py-1 rounded-full transition-colors duration-200 ${
          lang === "en" ? "bg-[#0A0A0F] text-white" : "text-[#62646B] hover:text-[#0A0A0F]"
        }`}
        aria-pressed={lang === "en"}
      >
        EN
      </button>
    </div>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const t = useT();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const reserveCta = { en: "Reserve →", sr: "Rezervišite →" };

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      data-testid="nav-container"
      style={{ top: "var(--wf-banner-h, 0px)" }}
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "wf-glass" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
        <a href="#hero" data-testid="nav-logo" className="flex items-center gap-3">
          <img
            src="/img/winn-coin.png"
            alt="Winfield Token logo"
            className="w-11 h-11 object-contain"
          />
          <span className="font-heading font-semibold text-xl tracking-tight">
            Winfield
          </span>
        </a>

        <div data-testid="nav-links" className="hidden lg:flex items-center gap-7">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.id}
              href={item.href}
              data-testid={`nav-link-${item.id}`}
              className="text-sm text-[#62646B] hover:text-[#0A0A0F] transition-colors duration-300"
            >
              {t(item.label)}
            </a>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <LangToggle variant="desktop" />
          <a
            href="/reserve"
            data-testid="nav-cta"
            className="px-5 py-2.5 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white text-sm font-semibold transition-colors duration-300"
          >
            {t(reserveCta)}
          </a>
        </div>

        <div className="lg:hidden flex items-center gap-3">
          <LangToggle variant="mobile" />
          <button
            data-testid="nav-mobile-toggle"
            className="text-[#0A0A0F]"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden wf-glass border-t border-[#E8E7E2] px-6 py-6 flex flex-col gap-4">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.id}
              href={item.href}
              onClick={() => setOpen(false)}
              className="text-[#62646B] hover:text-[#0A0A0F] transition-colors"
            >
              {t(item.label)}
            </a>
          ))}
          <a
            href="/reserve"
            onClick={() => setOpen(false)}
            className="mt-2 px-6 py-2.5 rounded-full bg-[#0A0A0F] text-white text-sm font-semibold text-center"
          >
            {t(reserveCta)}
          </a>
        </div>
      )}

      <motion.div
        data-testid="scroll-progress-bar"
        style={{ scaleX: scrollYProgress }}
        className="absolute bottom-0 left-0 right-0 h-[2px] origin-left bg-gradient-to-r from-[#8A8F9C] via-[#C9CDD6] to-white"
      />
    </motion.nav>
  );
};
